import React, { useEffect, useState } from 'react';
import { AppProvider, UIProvider, ThemeProvider, AuthProvider, SettingsProvider, ToastProvider } from '@context/index';
import { ToastContainer } from '@components/ui/ToastContainer';
import { componentRegistry } from '@ui-engine/index';
// Direct files, not the '@components/ui' barrel: the barrel re-exports every UI component (maths renderer, charts, ...)
// and importing it here put all of their libraries into the first download of every page.
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Text } from '@components/ui/Text';
import { Container } from '@components/ui/Container';
import { useUI } from '@context/UIContext';
import { config, validateConfig } from '@config/index';
import { MainLayout } from '@components/layouts';
import { Screen } from '@components/screens/Screen';
// Loaded on demand (each is its own chunk): they are only rendered inside the signed-in app, under AppRouter's
// <Suspense>, and importing them here pulled their heavy dependencies into the first download of every page.
const CourseCreator = React.lazy(() => import('@components/pages/CourseCreator').then(m => ({ default: m.CourseCreator })));
const Lab = React.lazy(() => import('@components/pages/Lab').then(m => ({ default: m.Lab })));
const UserManagement = React.lazy(() => import('@components/pages/UserManagement').then(m => ({ default: m.UserManagement })));
import { AppRouter } from '@components/routes/AppRouter';

/**
 * ========================================
 * AI BUDDY APP - MAIN APPLICATION FILE
 * ========================================
 * 
 * This is the root component that sets up the entire application.
 * 
 * Key Features:
 * 1. Component Registration - Registers all UI components with the engine
 * 2. Layout Structure - Uses MainLayout with Header, Sidebar, and Content
 * 3. Dynamic Screen Rendering - Loads and renders screens from JSON schemas
 * 4. Error Handling - Graceful error messages and loading states
 * 5. SPA Architecture - Single Page Application using dynamic screen loading
 * 
 * Architecture:
 * - AppProvider: Global app state management
 * - UIProvider: UI state and screen management
 * - MainLayout: Top-level layout (Header + Sidebar + Content)
 * - Dynamic Screen Rendering: Schemas loaded from /public/schemas/*.json
 */

// ========================================
// COMPONENT REGISTRATION
// ========================================
// Register UI components with the rendering engine
// This allows schemas to reference these components by name
componentRegistry.register('button', Button);
componentRegistry.register('input', Input);
componentRegistry.register('text', Text);
componentRegistry.register('container', Container);
// Register custom page components
componentRegistry.register('course', CourseCreator);
componentRegistry.register('lab', Lab);
componentRegistry.register('usermanagement', UserManagement);

// ========================================
// APP CONTENT COMPONENT
// ========================================
/**
 * AppContent Component
 * 
 * Handles the main app logic:
 * - Initialization (load first screen)
 * - State management (loading, errors)
 * - Screen rendering with layout
 */
const AppContent: React.FC = () => {
  const { currentScreen, isLoading, error, loadScreenFromURL } = useUI();
  const [initialized, setInitialized] = useState(false);

  // Initialize app on component mount
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Validate configuration
        validateConfig();
        
        // Load the dashboard as the initial screen
        // You can change this to 'home.json' if you prefer
        await loadScreenFromURL('/schemas/dashboard.json');
        setInitialized(true);
      } catch (err) {
        console.error('Failed to initialize app:', err);
      }
    };

    initializeApp();
  }, [loadScreenFromURL]);

  // ========================================
  // LOADING STATE
  // ========================================
  if (!initialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Text variant="p" className="text-gray-600">
          Loading {config.app.name}...
        </Text>
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-red-50">
        <Container maxWidth="md" padding="lg" centered className="text-center">
          <Text variant="h3" color="text-red-700" className="mb-4">
            Error Loading Screen
          </Text>
          <Text color="text-red-600">{error}</Text>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Reload App
          </button>
        </Container>
      </div>
    );
  }

  // ========================================
  // LOADING SCREEN
  // ========================================
  if (isLoading || !currentScreen) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <Text variant="p" className="text-gray-600">
            Loading screen...
          </Text>
        </div>
      </div>
    );
  }

  // ========================================
  // MAIN RENDER - WITH LAYOUT
  // ========================================
  // Renders the application with:
  // - Header (top with profile dropdown)
  // - Sidebar (left with navigation menu)
  // - Dynamic Screen Content (center/right)
  
  // Special handling for custom component pages
  if (currentScreen?.id === 'course_screen') {
    return (
      <MainLayout>
        <CourseCreator />
      </MainLayout>
    );
  }

  if (currentScreen?.id === 'lab_screen') {
    return (
      <MainLayout>
        <Lab />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Render the current screen schema using SchemaRenderer */}
      {currentScreen.schema && (
        <Screen schema={currentScreen.schema} context={{}} />
      )}
    </MainLayout>
  );
};

// ========================================
// ROOT APP COMPONENT
// ========================================
/**
 * App Component
 * 
 * Root component that wraps everything with providers:
 * - AuthProvider: Authentication state and session management
 * - AppProvider: Application state (config, global settings)
 * - UIProvider: UI state (current screen, loading, errors)
 * - ThemeProvider: Theme management
 * - AppRouter: Routing with authentication protection
 */
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppProvider>
        <UIProvider>
          <ThemeProvider>
            <SettingsProvider>
              <ToastProvider>
                <AppRouter appContent={<AppContent />} />
                <ToastContainer />
              </ToastProvider>
            </SettingsProvider>
          </ThemeProvider>
        </UIProvider>
      </AppProvider>
    </AuthProvider>
  );
};

export default App;
